import React, {
  FocusEvent,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import SavedAddressType, {
  ReservedSavedAddressType,
} from "@mapmycustomers/shared/enum/SavedAddressType";
import SavedAddress from "@mapmycustomers/shared/types/SavedAddress";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, TextField } from "@mapmycustomers/ui";

import savedAddressIcons from "@app/component/savedLocations/savedAddressIcons";
import { deleteSavedAddress, updateSavedAddress } from "@app/store/savedLocations/actions";
import {
  SavedLocationEvent,
  SavedLocationNameSource,
  trackSavedLocationEvent,
} from "@app/util/analytic/savedLocationsAnalytics";
import { TEXT_LENGTH_LIMIT } from "@app/util/consts";
import getSavedAddressDisplayName from "@app/util/savedLocations/getSavedAddressDisplayName";
import {
  hasSavedAddressErrorCode,
  RESERVED_SLOT_TAKEN_CODE,
} from "@app/util/savedLocations/savedAddressErrors";
import showReplaceSavedLocationConfirm from "@app/util/savedLocations/showReplaceSavedLocationConfirm";

import DismissButton from "../DismissButton";

import messages from "./messages";
import styles from "./NamingForm.module.scss";

const bold = (text: ReactNode) => <b>{text}</b>;
const selectAll = (event: FocusEvent<HTMLInputElement>) => event.target.select();

// Fires at most once, when the rep closes the card: renamed only after a real rename. Undo, or a
// card that closes on its own (replaced, row deleted or converted elsewhere), fires nothing
const trackNamed = (kind: SavedAddressType, renamed: boolean) =>
  trackSavedLocationEvent(SavedLocationEvent.NAMED, {
    kind,
    renamed,
    source: SavedLocationNameSource.NAME_STEP,
  });

export interface NamingFormProps {
  home?: SavedAddress;
  onDelete: typeof deleteSavedAddress.request;
  onHide: (payload: { id: SavedAddress["id"] }) => void;
  onUpdate: typeof updateSavedAddress.request;
  savedAddress: SavedAddress;
  work?: SavedAddress;
}

const NamingForm = ({ home, onDelete, onHide, onUpdate, savedAddress, work }: NamingFormProps) => {
  const intl = useIntl();
  const [initialName] = useState(savedAddress.name ?? "");
  const [name, setName] = useState(initialName);
  const [saving, startSaving, stopSaving] = useBoolean();
  // After a v1212 the list re-fetches; the replace is offered again once it holds a Home or Work
  // other than the one the server rejected
  const [retry, setRetry] = useState<{
    rejectedHolderId?: SavedAddress["id"];
    type: ReservedSavedAddressType;
  }>();
  const confirmRef = useRef<ReturnType<typeof showReplaceSavedLocationConfirm>>();
  const enterPressedRef = useRef(false);

  const hide = useCallback(() => onHide({ id: savedAddress.id }), [onHide, savedAddress.id]);

  const convert = useCallback(
    (type: ReservedSavedAddressType, replaceId?: SavedAddress["id"]) => {
      setRetry(undefined);
      startSaving();
      onUpdate({
        callback: (updatedAddress) => {
          trackNamed(updatedAddress.type, false);
          hide();
        },
        failureCallback: (error) => {
          stopSaving();
          if (hasSavedAddressErrorCode(error, RESERVED_SLOT_TAKEN_CODE)) {
            setRetry({ rejectedHolderId: replaceId, type });
          }
        },
        payload: { id: savedAddress.id, replaceId, type },
      });
    },
    [hide, onUpdate, savedAddress.id, startSaving, stopSaving]
  );

  const setType = useCallback(
    (type: ReservedSavedAddressType, holder?: SavedAddress) => {
      setRetry(undefined);
      if (holder) {
        confirmRef.current = showReplaceSavedLocationConfirm(intl, holder, () =>
          convert(type, holder.id)
        );
      } else {
        convert(type);
      }
    },
    [convert, intl]
  );

  const retryHolder = retry && (retry.type === SavedAddressType.HOME ? home : work);
  useEffect(() => {
    if (retry && !saving && retryHolder && retryHolder.id !== retry.rejectedHolderId) {
      setRetry(undefined);
      setType(retry.type, retryHolder);
    }
  }, [retry, retryHolder, saving, setType]);

  useEffect(() => () => confirmRef.current?.destroy(), []);

  const handleDismiss = () => {
    trackNamed(savedAddress.type, false);
    hide();
  };

  const handleSaveName = () => {
    const trimmedName = name.trim();
    if (!trimmedName || trimmedName === initialName || trimmedName === savedAddress.name) {
      handleDismiss();
      return;
    }
    setRetry(undefined);
    startSaving();
    onUpdate({
      callback: () => {
        trackNamed(savedAddress.type, true);
        hide();
      },
      failureCallback: stopSaving,
      payload: { id: savedAddress.id, name: trimmedName },
    });
  };

  // Saves on keyup, so the card is still there to stop it reaching universal search. An Enter that
  // ends an input-method composition doesn't count; Safari marks that one only with keyCode 229
  const handleNameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    enterPressedRef.current =
      event.key === "Enter" && !event.nativeEvent.isComposing && event.keyCode !== 229;
  };
  const handleNameKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && enterPressedRef.current) {
      enterPressedRef.current = false;
      handleSaveName();
    }
  };

  const handleUndo = () => {
    setRetry(undefined);
    startSaving();
    onDelete({ id: savedAddress.id, callback: hide, failureCallback: stopSaving });
  };

  return (
    <>
      <div className={styles.message}>
        {intl.formatMessage(messages.saved, {
          b: bold,
          name: getSavedAddressDisplayName(intl, savedAddress),
        })}
      </div>
      <DismissButton disabled={saving} onClick={handleDismiss} />
      <div className={styles.row}>
        <TextField
          aria-label={intl.formatMessage(messages.nameLabel)}
          className={styles.name}
          disabled={saving}
          maxLength={TEXT_LENGTH_LIMIT}
          onChange={setName}
          onFocus={selectAll}
          onKeyDown={handleNameKeyDown}
          onKeyUp={handleNameKeyUp}
          placeholder={intl.formatMessage(messages.namePlaceholder)}
          value={name}
        />
        <Button disabled={saving} onClick={handleSaveName} type="primary">
          {intl.formatMessage(messages.saveName)}
        </Button>
      </div>
      <div className={styles.row}>
        <Button
          disabled={saving}
          icon={<FontAwesomeIcon icon={savedAddressIcons[SavedAddressType.HOME]} />}
          onClick={() => setType(SavedAddressType.HOME, home)}
        >
          {intl.formatMessage(messages.setHome)}
        </Button>
        <Button
          disabled={saving}
          icon={<FontAwesomeIcon icon={savedAddressIcons[SavedAddressType.WORK]} />}
          onClick={() => setType(SavedAddressType.WORK, work)}
        >
          {intl.formatMessage(messages.setWork)}
        </Button>
        <Button className={styles.undo} disabled={saving} onClick={handleUndo} type="text">
          {intl.formatMessage(messages.undo)}
        </Button>
      </div>
    </>
  );
};

export default NamingForm;
